/**
 * Plans, as the app has it: pick a team and a plan; the plan's header with its id, objective,
 * the Plan > Apply > Unify loop and its status; progress over its tasks; and the waves of the
 * dependency graph. Nodes arrive with `plans.get`; a workbench without it shows the counts.
 */

import { dateTime } from '@aioe/shared/wording/dateTime';
import { Circle, CircleCheck, CircleCheckFill, CircleExclamation, ListCheck, NodesRight, Play } from '@gravity-ui/icons';
import { useState } from 'react';

import { PlanGraph, stepStatus } from '../../components/plans/PlanGraph.tsx';
import { Button, ChoiceSelect, Empty, ListBlock, ListGroup, ListRow, Stat, StatusMark } from '../../components/ui.tsx';
import { useLoad } from '../../hooks.ts';
import { useMoney } from '../../money.tsx';
import type { Plan, Task, Workbench } from '../useWorkbench.ts';
import { TeamPicker, useTeamChoice } from './TeamPicker.tsx';
import { Markdown } from '../../components/Markdown.tsx';
import { RemoteView } from './RemoteView.tsx';

interface PlanNode {
  ref: string;
  taskId?: string;
  title: string;
  assignee?: string;
  role?: string;
  dependsOn?: string[];
  estimate?: { complexity?: number; tokens?: number };
  status?: string;
}
interface FullPlan extends Omit<Plan, 'nodes'> {
  nodes: PlanNode[];
  criticalPath?: string[];
  acceptance?: Array<{ id: string; title?: string; passed?: boolean }>;
  summary?: string;
  /** Where the plan is in the PAUL loop: plan, apply, unify, complete. */
  loop?: 'plan' | 'apply' | 'unify' | 'complete' | string;
  ref?: string;
  track?: string;
  estimate?: { tokens?: number; usd?: number; points?: number; basis?: { sampleTasks?: number } };
  progress?: { done: number; total: number };
  unify?: { at: string; summary?: string; tasks?: { planned: number; done: number } };
}

const STEPS = ['Plan', 'Apply', 'Unify'] as const;
function stepsFromLoop(loop: string | undefined, status: string): number {
  if (loop === 'complete') return 3;
  if (loop === 'unify') return 2;
  if (loop === 'apply') return 1;
  if (loop === 'plan') return 0;
  return stepsDone(status);
}
function stepsDone(status: string): number {
  if (status === 'draft') return 0;
  if (status === 'approved' || status === 'dispatched') return 1;
  if (status === 'done') return 2;
  if (status === 'verified') return 3;
  return 0;
}

function waves(nodes: PlanNode[]): PlanNode[][] {
  const depth = new Map<string, number>();
  const byRef = new Map(nodes.map((n) => [n.ref, n]));
  const d = (n: PlanNode, seen = new Set<string>()): number => {
    if (depth.has(n.ref)) return depth.get(n.ref)!;
    if (seen.has(n.ref)) return 0;
    seen.add(n.ref);
    const v = Math.max(0, ...(n.dependsOn ?? []).map((r) => (byRef.get(r) ? d(byRef.get(r)!, seen) + 1 : 0)));
    depth.set(n.ref, v);
    return v;
  };
  nodes.forEach((n) => d(n));
  const out: PlanNode[][] = [];
  nodes.forEach((n) => (out[depth.get(n.ref) ?? 0] ??= []).push(n));
  return out.filter(Boolean);
}

export function PlansView({ w }: { w: Workbench }) {
  const { format } = useMoney();
  const [teamId, setTeamId] = useTeamChoice(w);
  const plans = w.useView<Plan[]>('plans.list', { teamId }, [teamId], 20_000);
  const tasks = w.useView<Task[]>('board.list', { teamId }, [teamId], 20_000);
  const [planId, setPlanId] = useState<string>();
  const plan = (plans.data ?? []).find((p) => p.id === planId) ?? plans.data?.[0];
  const full = useLoad<FullPlan | undefined>(async () => (plan && w.connected ? w.tryCall<FullPlan>('plans.get', { teamId, planId: plan.id }) : undefined), [plan?.id, w.connected], 20_000);
  const nodes = full.data?.nodes ?? [];
  const taskById = new Map((tasks.data ?? []).map((t) => [t.id, t]));
  const nodeStatus = (n: PlanNode) => (n.taskId ? taskById.get(n.taskId)?.status : undefined) ?? n.status ?? 'todo';
  const done = full.data?.progress?.done ?? nodes.filter((n) => nodeStatus(n) === 'done').length;
  const inProgress = nodes.filter((n) => nodeStatus(n) === 'in-progress').length;
  const blocked = nodes.filter((n) => nodeStatus(n) === 'blocked').length;
  const critical = new Set(full.data?.criticalPath ?? []);
  const total = nodes.length || plan?.nodes || 0;
  const totalForPct = full.data?.progress?.total ?? nodes.length;
  const pct = totalForPct ? Math.round((done / totalForPct) * 100) : plan && stepsDone(plan.status) >= 2 ? 100 : 0;
  const steps = plan ? stepsFromLoop(full.data?.loop, plan.status) : 0;
  const est = full.data?.estimate;
  const idx = plans.data ? plans.data.findIndex((p) => p.id === plan?.id) + 1 : 0;

  const stuck = blocked > 0;
  const passed = full.data?.acceptance?.filter((x) => x.passed === true).length ?? 0;

  return (
    <RemoteView
      actions={
        <>
          <TeamPicker w={w} teamId={teamId} onChange={setTeamId} />
          {(plans.data?.length ?? 0) > 1 && (
            <ChoiceSelect label="Plan" className="w-fit min-w-44 max-w-xl" value={plan?.id} options={Object.fromEntries(plans.data!.map((p) => [p.id, p.objective.slice(0, 90)]))} onChange={setPlanId} />
          )}
        </>
      }
    >
      {!plan ? (
        <Empty tone="amber" icon={<NodesRight />} title="No plans for this team" body="Plans come from Team Work: describe the work and the operations manager drafts one for your approval." />
      ) : (
        <>
          <ListGroup
            title={full.data?.ref ?? `PLAN-${idx}`}
            description={full.data?.track}
            actions={
              plan.status === 'draft' ? (
                <Button size="sm" variant="primary" onPress={() => void w.act('Approve plan', () => w.call('plans.approve', { teamId, planId: plan.id }), plans.reload)}>
                  Approve
                </Button>
              ) : undefined
            }
          >
            <ListRow
              title={plan.objective}
              description={[
                `${total} ${total === 1 ? 'task' : 'tasks'}`,
                est?.points !== undefined ? `${est.points} pt` : undefined,
                est?.tokens !== undefined ? `est. ~${Math.round(est.tokens / 1000)}k tokens` : undefined,
                est?.usd !== undefined ? `~${format(est.usd)}${est.basis?.sampleTasks ? ` (from ${est.basis.sampleTasks} past task windows)` : ''}` : undefined,
                `by ${plan.author}`,
                dateTime(plan.createdAt),
              ]
                .filter(Boolean)
                .join(' · ')}
              status={<StatusMark state={plan.status === 'abandoned' ? 'idle' : plan.status === 'draft' ? 'medium' : 'low'} label={plan.status === 'draft' ? 'Draft, waiting for approval' : `${plan.status[0]!.toUpperCase()}${plan.status.slice(1)}`} />}
            />
            <ListBlock>
              <ol className="m-0 flex list-none flex-wrap items-center gap-1.5 p-0 text-sm" aria-label="Plan, apply, unify">
                {STEPS.map((step, i) => (
                  <li key={step} className="flex items-center gap-1.5">
                    <span className={`flex items-center gap-1 ${i < steps ? 'text-foreground' : 'text-muted'}`}>
                      {i < steps ? <CircleCheckFill className="size-3.5 text-success" aria-label="done" /> : <Circle className="size-3.5" aria-label="to come" />}
                      {step}
                    </span>
                    {i < STEPS.length - 1 && <span className="text-muted" aria-hidden="true">›</span>}
                  </li>
                ))}
              </ol>
            </ListBlock>
            {plan.summary ? (
              <ListBlock>
                <Markdown compact className="text-muted" text={plan.summary} />
              </ListBlock>
            ) : null}
          </ListGroup>
          <div className="art-stats">
            <Stat tone="amber" icon={<ListCheck />} label="Tasks" value={total} detail={nodes.length ? `${Math.max(0, nodes.length - done - inProgress - blocked)} still to start` : 'in this plan'} />
            <Stat tone="green" icon={<CircleCheck />} label="Done" value={nodes.length || full.data?.progress ? done : undefined} detail={`${pct}% of the plan`} />
            <Stat tone="blue" icon={<Play />} label="In progress" value={nodes.length ? inProgress : undefined} detail="being worked on" />
            <Stat tone="rose" icon={<CircleExclamation />} label="Blocked" value={nodes.length ? blocked : undefined} detail={stuck ? 'waiting on a person or a fix' : 'nothing blocked'} needsYou={stuck} />
          </div>
          {full.data?.acceptance?.length ? (
            <ListGroup title="Acceptance" count={`${passed} of ${full.data.acceptance.length} passed`} description="What has to hold for the plan to be done.">
              {full.data.acceptance.map((x) => (
                <ListRow key={x.id} title={x.title ?? x.id} description={`Acceptance check ${x.id}`} status={<StatusMark state={x.passed === true ? 'low' : x.passed === false ? 'error' : 'idle'} label={x.passed === true ? 'Passed' : x.passed === false ? 'Failed' : 'Not yet checked'} />} />
              ))}
            </ListGroup>
          ) : null}
          {full.data?.unify && (
            <ListGroup title="Unified" count={full.data.unify.tasks ? `${full.data.unify.tasks.done} of ${full.data.unify.tasks.planned} done` : undefined} description={`Brought together ${dateTime(full.data.unify.at)}.`}>
              <ListBlock>{full.data.unify.summary ? <Markdown compact text={full.data.unify.summary} /> : <p className="m-0 text-sm text-muted">The plan was brought together.</p>}</ListBlock>
            </ListGroup>
          )}
          {/* The workbench's own plan graph (PlanGraph): waves of work, what each step waits on, the critical path. */}
          <ListGroup title="Waves" count={nodes.length ? waves(nodes).length : undefined} description={critical.size ? 'What each step waits on; the critical path is outlined. Pick a step to see it.' : 'What each step waits on. Pick a step to see it.'}>
            {nodes.length ? (
              <ListBlock>
                <PlanGraph
                  planId={`${teamId}/${plan.id}`}
                  steps={nodes.map((n) => {
                    const status = stepStatus(nodeStatus(n));
                    return { ref: n.ref, title: n.title, dependsOn: n.dependsOn ?? [], ...(status ? { status } : {}), ...(n.taskId ? { taskId: n.taskId } : {}), ...(n.assignee ? { assignee: n.assignee } : {}), ...(n.role ? { role: n.role } : {}), ...(n.estimate ? { estimate: n.estimate } : {}) };
                  })}
                  critical={full.data?.criticalPath ?? []}
                  criteria={(full.data?.acceptance ?? []).map((x) => ({ id: x.id, title: x.title ?? x.id, passed: x.passed === true }))}
                />
              </ListBlock>
            ) : (
              <ListBlock className="text-sm text-muted">The waves of the dependency graph appear here once the workbench shares a plan's steps; the app shows them today.</ListBlock>
            )}
          </ListGroup>
        </>
      )}
    </RemoteView>
  );
}
